import React, { useMemo, useState } from "react";
import { Alert, Box } from "@mui/material";
import {
  Area, AreaChart, Bar, BarChart, CartesianGrid, Cell, ComposedChart,
  LabelList, Line, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";

import {
  getResponseSpeedSummary, getResponseSpeedTrend,
  getResponseSpeedDistribution, getSlowestResponses,
} from "../../services/ops/opsservices";
import { useClientTimeZone, zoneAbbr } from "../../utils/clienttimezone";
import ConversationDialog from "./ConversationDialog";
import {
  BLUE, GREEN, AMBER, RED, NAVY, AVG_INK,
  CounterCard, ChartCard, SectionTitle, Caption, DetailTable, LoadingRow,
  OpsSlicers, identityColumns, drillColumn, useOpsData,
  DEFAULT_GRAN, DEFAULT_RANGE, getDateRange,
  formatRangeLabel, isSubDayGran,
  fmtInt, fmtPct, fmtDuration, fmtDate, fmtAxisDate, fmtTime, safeNum,
} from "./opsShared";

// A healthy turn completes in ~7 seconds. 95% under a minute is the line
// between "working" and "something is wrong" — on 14 Aug, with the content
// filter blocking replies, this read 54%.
const TARGET_UNDER_60 = 95;

export default function ResponseSpeedDashboard() {
  const [gran,  setGran]  = useState(DEFAULT_GRAN);
  const [range, setRange] = useState(DEFAULT_RANGE);

  const { timeZone, tzReady } = useClientTimeZone();
  const tzLabel = zoneAbbr(timeZone);

  const params = useMemo(() => ({
    ...getDateRange(range, timeZone),
    granularity: gran.toLowerCase(),
    // No tz param: the backend resolves the tenant zone from clientconfig, so
    // every viewer sees the same day boundaries regardless of their browser.
  }), [range, gran, timeZone]);

  // Any account row opens its full conversation.
  const [drill, setDrill] = useState(null);
  const openThread = (row) => setDrill(row);

  const { data, loading, error } = useOpsData({
    summary:      getResponseSpeedSummary,
    trend:        getResponseSpeedTrend,
    distribution: getResponseSpeedDistribution,
    slowest:      getSlowestResponses,
  }, params, tzReady);

  const s = data.summary || {};
  const pctUnder = safeNum(s.answeredUnderOneMinutePct);
  const pctStatus = pctUnder >= TARGET_UNDER_60 ? "ok" : pctUnder >= 80 ? "warn" : "bad";

  const trend = (data.trend || []).map((r) => ({
    date: fmtAxisDate(r.summary_date, gran, timeZone),
    answered: safeNum(r.conversationsAnswered),
    typical:  Math.round(safeNum(r.typicalSeconds)),
    slowest5: Math.round(safeNum(r.slowestFivePctSeconds)),
    pct:      Math.round(safeNum(r.answeredUnderOneMinutePct)),
  }));

  const dist = (data.distribution || []).map((r) => ({
    label: r.label, conversations: safeNum(r.conversations),
  }));

  const avgPct = trend.length
    ? trend.reduce((a, r) => a + r.pct, 0) / trend.length : 0;

  const rangeLabel = formatRangeLabel(params, timeZone);
  const titleOf = (t) => `${t} · ${rangeLabel}`;

  const slowestColumns = [
    drillColumn(openThread),
    ...identityColumns,
    { key: "customerMessage", label: "Customer's Message", width: 200 },
    { key: "responseSeconds", label: "Response Time", width: 110, align: "right",
      render: (r) => fmtDuration(r.responseSeconds) },
    { key: "deliveryAttempts", label: "Attempts", width: 80, align: "right",
      render: (r) => safeNum(r.deliveryAttempts) },
    { key: "receivedUtc", label: "Received", width: 160,
      render: (r) => fmtTime(r.receivedUtc, timeZone) },
    { key: "answeredUtc", label: "Answered", width: 160,
      render: (r) => fmtTime(r.answeredUtc, timeZone) },
    { key: "agentReply", label: "AI's Reply", width: 280 },
  ];

  return (
    <Box sx={{ p: 2 }}>
      <OpsSlicers gran={gran} setGran={setGran} range={range} setRange={setRange} tzLabel={tzLabel} />

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && <LoadingRow text="Measuring response times…" />}

      {!loading && (
        <>
          <SectionTitle>Response Speed</SectionTitle>
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(5, minmax(0,1fr))", gap: 1, mb: 1.5 }}>
            <CounterCard label="Conversations Answered" value={fmtInt(s.conversationsAnswered)} color={NAVY} />
            <CounterCard label="Typical Response Time"  value={fmtDuration(s.typicalSeconds)} color={BLUE}
              sub="Median — half were faster" />
            <CounterCard label="Slowest 5%"             value={fmtDuration(s.slowestFivePctSeconds)} color={AMBER} />
            <CounterCard label="Longest Response"       value={fmtDuration(s.longestSeconds)} color={AMBER} />
            <CounterCard label="Answered Under 1 Minute" value={fmtPct(pctUnder)} color={GREEN}
              status={pctStatus} sub={`Target ${TARGET_UNDER_60}%`} />
          </Box>

          <Box sx={{ display: "grid", gridTemplateColumns: "3fr 2fr", gap: 1.5, mb: 1.5 }}>
            <ChartCard title={titleOf("Answered under 1 minute")}
              right={`avg ${fmtPct(avgPct)}`} height={280}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={trend} margin={{ top: 18, right: 8, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="pctGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={GREEN} stopOpacity={0.18} />
                      <stop offset="95%" stopColor={GREEN} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" />
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#888" }} interval="preserveStartEnd" minTickGap={24} />
                  <YAxis tick={{ fontSize: 10, fill: "#888" }} domain={[0, 100]}
                    tickFormatter={(v) => `${v}%`} />
                  <Tooltip contentStyle={{ fontSize: 12 }} formatter={(v) => [`${v}%`, "Under 1 min"]} />
                  {/* The target line, not an average — this chart has a number
                      it is supposed to hit, so the rule shows the bar rather
                      than restating the data. */}
                  <ReferenceLine y={TARGET_UNDER_60} stroke={AVG_INK} strokeDasharray="3 3" strokeWidth={1} />
                  <Area type="monotone" dataKey="pct" name="Under 1 min"
                    stroke={GREEN} fill="url(#pctGrad)" strokeWidth={2} dot={{ r: 3 }}>
                    <LabelList dataKey="pct" position="top" offset={8}
                      fontSize={10} fill={GREEN} formatter={(v) => `${v}%`} />
                  </Area>
                </AreaChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title={titleOf("Response time distribution")} height={280}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={dist} margin={{ top: 18, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" />
                  <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#888" }} />
                  <YAxis tick={{ fontSize: 10, fill: "#888" }} />
                  <Tooltip contentStyle={{ fontSize: 12 }} />
                  <Bar dataKey="conversations" name="Conversations" radius={[3, 3, 0, 0]}>
                    {/* Green through amber to red as the bucket gets slower —
                        the ramp is the message, so the reader does not have to
                        parse the axis labels to see where the mass sits. */}
                    {dist.map((_, i) => (
                      <Cell key={i} fill={[GREEN, "#5FB894", AMBER, "#C4844A", "#B85C3C", RED][i] || RED} />
                    ))}
                    <LabelList dataKey="conversations" position="top" fontSize={10} fill="#888" />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </Box>
          <Caption>
            A bimodal split — a tall bar under 30 seconds and another past 15 minutes with
            nothing between — is the signature of replies being rejected rather than running
            slowly. That was the shape on 14 August, when Azure's jailbreak filter was
            blocking AI's prompt.
          </Caption>

          <Box sx={{ mb: 1.5, mt: 1.5 }}>
            <ChartCard title={titleOf("Typical vs slowest response time")} height={260}>
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={trend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" />
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#888" }} interval="preserveStartEnd" minTickGap={24} />
                  <YAxis yAxisId="left" tick={{ fontSize: 10, fill: "#888" }}
                    tickFormatter={(v) => `${v}s`} />
                  <YAxis yAxisId="right" orientation="right"
                    tick={{ fontSize: 10, fill: NAVY }} />
                  <Tooltip contentStyle={{ fontSize: 12 }} />
                  <Bar yAxisId="right" dataKey="answered" name="Conversations"
                    fill={`${NAVY}33`} radius={[3, 3, 0, 0]} />
                  <Line yAxisId="left" type="monotone" dataKey="typical" name="Typical (sec)"
                    stroke={BLUE} strokeWidth={2} dot={{ r: 3 }} />
                  <Line yAxisId="left" type="monotone" dataKey="slowest5" name="Slowest 5% (sec)"
                    stroke={AMBER} strokeWidth={2} strokeDasharray="4 3" dot={{ r: 2 }} />
                </ComposedChart>
              </ResponsiveContainer>
            </ChartCard>
            <Caption>
              Volume sits behind the lines so a slow day can be read against how busy it was.
              Typical and Slowest 5% separating sharply means a subset of turns is failing,
              not that everything is degrading.
            </Caption>
          </Box>

          <SectionTitle right={`${(data.slowest || []).length} shown · slowest first`}>
            Slowest Conversations
          </SectionTitle>
          <DetailTable
            columns={slowestColumns}
            rows={data.slowest || []}
            emptyText="No conversations in this range."
            maxHeight={440}
          />
        </>
      )}

      <ConversationDialog
        open={!!drill}
        onClose={() => setDrill(null)}
        customerdataid={drill?.customerdataid}
      />
    </Box>
  );
}